import { useCallback } from 'react';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

/** How a row / cell differs from a reference state (catch-up mode, decision C4). */
export type CellMark = 'added' | 'changed';

export interface RowMark {
  kind: CellMark;
  /** Changed column keys; null = the whole row (added rows, or no column-level detail). */
  columns: ReadonlySet<string> | null;
}

/** Row marks per grid id (`DataGrid` `id`), then per row key. */
export type GridMarks = Readonly<Record<string, ReadonlyMap<string, RowMark>>>;

/**
 * Cross-feature highlight of changed table cells: another feature (catch-up) publishes marks,
 * every DataGrid asks {@link useCellMarks} for its own grid id. Null = no highlighting.
 */
export const tableHighlightStore = createStore<{
  marks: GridMarks | null;
  setMarks(marks: GridMarks | null): void;
}>()((set) => ({
  marks: null,
  setMarks: (marks) => set({ marks }),
}));

/** Mark of one cell: added rows mark every cell, changed rows only the changed columns. */
export function cellMark(
  marks: GridMarks | null,
  gridId: string,
  rowKey: string,
  column: string,
): CellMark | null {
  const m = marks?.[gridId]?.get(rowKey);
  if (!m) return null;
  return m.columns === null || m.columns.has(column) ? m.kind : null;
}

/** `(rowKey, columnKey) => mark` for one grid; re-renders when the marks change. */
export function useCellMarks(gridId: string): (rowKey: string, column: string) => CellMark | null {
  const marks = useStore(tableHighlightStore, (s) => s.marks);
  return useCallback(
    (rowKey: string, column: string) => cellMark(marks, gridId, rowKey, column),
    [marks, gridId],
  );
}
